// ============================================================
// odpt:Train (列車区間情報) のポーリング
// ============================================================
// 列車は緯度経度を持たない区間表現なので、駅マスタの位置に
// fromStation の座標を割り当ててプロットする (近似)。
// 真の補間は odpt:Railway の lineString を使うのが正しいが、PoC では駅位置で十分。

import type { Entity } from '../types'
import { POLL_TRAIN_MS } from '../config'
import { fetchStations, fetchTrains, type OdptStation, type OdptTrain } from './odpt-client'

export type TrainHandler = (trains: Entity[]) => void

export class TrainStateClient {
  private timer: ReturnType<typeof setInterval> | null = null
  private handlers = new Set<TrainHandler>()
  private stationsById = new Map<string, OdptStation>()
  private inFlight = false

  async initStations(operatorIds?: string[]): Promise<void> {
    const stations = await fetchStations(operatorIds)
    this.stationsById = new Map(stations.map((s) => [s['owl:sameAs'], s]))
  }

  start(): void {
    if (this.timer) return
    void this.pollOnce()
    this.timer = setInterval(() => void this.pollOnce(), POLL_TRAIN_MS)
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  subscribe(handler: TrainHandler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  // ───────── private ─────────

  private async pollOnce(): Promise<void> {
    if (this.inFlight) return
    this.inFlight = true
    try {
      const trains = await fetchTrains()
      const entities: Entity[] = []
      for (const t of trains) {
        const e = this.materialize(t)
        if (e) entities.push(e)
      }
      this.handlers.forEach((h) => h(entities))
    } catch (err) {
      console.warn('[train-state-client] failed', err)
    } finally {
      this.inFlight = false
    }
  }

  private materialize(t: OdptTrain): Entity | null {
    const fromId = t['odpt:fromStation']
    if (!fromId) return null
    const s = this.stationsById.get(fromId)
    if (!s) return null
    const railway = t['odpt:railway']?.split(':').pop() ?? '電車'
    const toLabel = t['odpt:toStation']?.split('.').pop() ?? ''
    return {
      id: `train:${t['owl:sameAs']}`,
      kind: 'train',
      lat: s['geo:lat'],
      lon: s['geo:long'],
      moving: true,
      label: `${railway} / ${toLabel}`,
    }
  }
}
